import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { supabase } from '../lib/supabase'
import '../styles/LogRound.css'

const STEPS = 5

function NumberInput({ value, onChange, min = 0, max = 99 }) {
  return (
    <div className="number-input">
      <button className="num-btn" onClick={() => onChange(Math.max(min, value - 1))}>−</button>
      <span className="num-display">{value}</span>
      <button className="num-btn" onClick={() => onChange(Math.min(max, value + 1))}>+</button>
    </div>
  )
}

function PillGroup({ options, value, onChange }) {
  return (
    <div className="pill-group">
      {options.map(o => (
        <div key={o.value} className={`pill ${value === o.value ? 'selected' : ''}`} onClick={() => onChange(o.value)}>
          {o.label}
        </div>
      ))}
    </div>
  )
}

function Toggle({ value, onChange, options }) {
  return (
    <div className="toggle-group">
      {options.map(o => (
        <button key={o.value} className={`toggle-btn ${value === o.value ? 'active' : ''}`} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  )
}

export default function LogRound() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [step, setStep] = useState(1)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const [form, setForm] = useState({
    holes: 18, course: '', score_mode: 'gross', score: '', over_under: 0, par: 72,
    fw_hit: 7, driver_miss_left: 0, driver_miss_right: 0, driver_miss_straight: 7, driver_dist: '220-240',
    gir: 6, approach_left: 2, approach_right: 2, approach_short: 4, approach_long: 1,
    ud_att: 6, ud_made: 2, bunkers: 2, bunker_saves: 0,
    one_putts: 4, two_putts: 11, three_putts: 3, putt_issue: 'distance'
  })

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const maxHoles = form.holes
  const maxFairways = form.holes === 9 ? 7 : 14
  const totalPutts = form.one_putts + form.two_putts + form.three_putts

  const handleHolesChange = (h) => {
    setForm(f => ({
      ...f, holes: h,
      fw_hit: h === 9 ? 4 : 7,
      driver_miss_straight: h === 9 ? 4 : 7,
      gir: h === 9 ? 3 : 6,
      ud_att: h === 9 ? 3 : 6, ud_made: h === 9 ? 1 : 2,
      approach_left: h === 9 ? 1 : 2, approach_right: h === 9 ? 1 : 2,
      approach_short: h === 9 ? 2 : 4, approach_long: h === 9 ? 1 : 1,
      one_putts: h === 9 ? 2 : 4, two_putts: h === 9 ? 6 : 11, three_putts: h === 9 ? 1 : 3,
    }))
  }

  const progress = (step / STEPS) * 100
  const next = () => { window.scrollTo(0, 0); setStep(s => s + 1) }
  const back = () => { window.scrollTo(0, 0); setStep(s => s - 1) }

  const getGrossScore = () => {
    if (form.score_mode === 'gross') return parseInt(form.score) || form.par
    return form.par + form.over_under
  }

  const getDominantMiss = (left, right, short, long) => {
    const vals = { left, right, short, long }
    const max = Math.max(left, right, short, long)
    if (max === 0) return 'none'
    return Object.keys(vals).find(k => vals[k] === max)
  }

  const save = async () => {
    if (form.score_mode === 'gross' && !form.score) return setError('Please enter your score')
    setSaving(true); setError('')
    const grossScore = getGrossScore()
    const totalDriverMisses = form.driver_miss_left + form.driver_miss_right
    const dominantDriverMiss = totalDriverMisses === 0 ? 'none' : form.driver_miss_left > form.driver_miss_right ? 'left' : form.driver_miss_right > form.driver_miss_left ? 'right' : 'both'
    const dominantApproachMiss = getDominantMiss(form.approach_left, form.approach_right, form.approach_short, form.approach_long)
    const totalApproachMisses = form.approach_left + form.approach_right + form.approach_short + form.approach_long
    const approachDisp = totalApproachMisses === 0 ? 'tight' : totalApproachMisses > (maxHoles * 0.5) ? 'scattered' : 'moderate'

    const { data, error } = await supabase.from('rounds').insert({
      user_id: user.id,
      course: form.course || 'Unknown Course',
      holes: form.holes,
      score: grossScore,
      par: form.par,
      fw: form.fw_hit,
      fw_total: maxFairways,
      gir: form.gir,
      putts: totalPutts,
      one_putts: form.one_putts,
      two_putts: form.two_putts,
      three_putts: form.three_putts,
      ud_att: form.ud_att,
      ud_made: form.ud_made,
      bunkers: form.bunkers,
      bunker_saves: form.bunker_saves,
      driver_miss_left: form.driver_miss_left,
      driver_miss_right: form.driver_miss_right,
      driver_miss_straight: form.driver_miss_straight,
      driver_dist: form.driver_dist,
      driver_miss: dominantDriverMiss,
      approach_left: form.approach_left,
      approach_right: form.approach_right,
      approach_short: form.approach_short,
      approach_long: form.approach_long,
      approach_miss: dominantApproachMiss,
      approach_disp: approachDisp,
      putt_issue: form.putt_issue,
      played_at: new Date().toISOString()
    }).select().single()

    if (error) { setError(error.message); setSaving(false) }
    else navigate(`/results/${data.id}`)
  }

  return (
    <div className="log-round">
      <div className="log-header">
        <h2>Log Round</h2>
        <p>Enter your stats to get your full analysis</p>
      </div>
      <div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%` }} /></div>
      <div className="progress-label">Step {step} of {STEPS}</div>
      {error && <div className="error-msg">{error}</div>}

      {step === 1 && (
        <div className="step-card">
          <div className="step-title">Course & Score</div>
          <div className="step-sub">Tell us about your round</div>

          <div className="form-group">
            <label className="form-label">How many holes did you play?</label>
            <Toggle value={form.holes} onChange={handleHolesChange} options={[{value:9,label:'9 Holes'},{value:18,label:'18 Holes'}]} />
          </div>

          <div className="form-group">
            <label className="form-label">Course Name</label>
            <input className="form-input" placeholder="e.g. Wentworth East" value={form.course} onChange={e => set('course', e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">Course Par</label>
            <PillGroup
              options={[{value:67,label:'Par 67'},{value:68,label:'Par 68'},{value:69,label:'Par 69'},{value:70,label:'Par 70'},{value:71,label:'Par 71'},{value:72,label:'Par 72'},{value:73,label:'Par 73'}]}
              value={form.par} onChange={v => set('par', v)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">How do you want to enter your score?</label>
            <Toggle value={form.score_mode} onChange={v => set('score_mode', v)} options={[{value:'gross',label:'Total Score'},{value:'over_under',label:'Over / Under Par'}]} />
          </div>

          {form.score_mode === 'gross' ? (
            <div className="form-group">
              <label className="form-label">Total Score (Gross)</label>
              <input className="form-input" type="number" placeholder={`e.g. ${form.par + 10}`} value={form.score} onChange={e => set('score', e.target.value)} />
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Over / Under Par</label>
              <div className="over-under-row">
                <button className="ou-btn" onClick={() => set('over_under', form.over_under - 1)}>−</button>
                <div className="ou-display">
                  <span className={`ou-num ${form.over_under > 0 ? 'over' : form.over_under < 0 ? 'under' : 'even'}`}>
                    {form.over_under > 0 ? `+${form.over_under}` : form.over_under === 0 ? 'E' : form.over_under}
                  </span>
                  <span className="ou-label">{form.over_under > 0 ? 'Over Par' : form.over_under < 0 ? 'Under Par' : 'Level Par'}</span>
                </div>
                <button className="ou-btn" onClick={() => set('over_under', form.over_under + 1)}>+</button>
              </div>
              <p className="score-hint">Gross score: {form.par + form.over_under}</p>
            </div>
          )}

          <div className="step-nav">
            <button className="btn-orange full" onClick={next}>Next →</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="step-card">
          <div className="step-title">Driving</div>
          <div className="step-sub">Tee shots & accuracy off the tee</div>

          <div className="form-group">
            <label className="form-label">Fairways Hit (out of {maxFairways} driveable holes)</label>
            <div className="input-row">
              <span className="input-label">Fairways hit</span>
              <NumberInput value={form.fw_hit} onChange={v => set('fw_hit', v)} min={0} max={maxFairways} />
            </div>
            <p className="field-hint">{maxFairways - form.fw_hit} missed · FIR: {Math.round((form.fw_hit / maxFairways) * 100)}%</p>
          </div>

          <div className="form-group">
            <label className="form-label">Missed fairways — how many each direction?</label>
            <div className="input-row"><span className="input-label">🔴 Missed left</span><NumberInput value={form.driver_miss_left} onChange={v => set('driver_miss_left', v)} min={0} max={maxFairways} /></div>
            <div className="input-row" style={{marginTop:8}}><span className="input-label">🔴 Missed right</span><NumberInput value={form.driver_miss_right} onChange={v => set('driver_miss_right', v)} min={0} max={maxFairways} /></div>
            <div className="input-row" style={{marginTop:8}}><span className="input-label">✅ Hit fairway</span><NumberInput value={form.driver_miss_straight} onChange={v => set('driver_miss_straight', v)} min={0} max={maxFairways} /></div>
          </div>

          <div className="form-group">
            <label className="form-label">Average driver distance (yards)</label>
            <PillGroup
              options={[{value:'under220',label:'Under 220'},{value:'220-240',label:'220–240'},{value:'240-260',label:'240–260'},{value:'over260',label:'260+'}]}
              value={form.driver_dist} onChange={v => set('driver_dist', v)}
            />
          </div>

          <div className="step-nav">
            <button className="btn-secondary" onClick={back}>← Back</button>
            <button className="btn-orange" onClick={next}>Next →</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="step-card">
          <div className="step-title">Approach Shots</div>
          <div className="step-sub">Iron play & greens in regulation</div>

          <div className="form-group">
            <label className="form-label">Greens in Regulation (out of {maxHoles})</label>
            <div className="input-row">
              <span className="input-label">GIR</span>
              <NumberInput value={form.gir} onChange={v => set('gir', v)} min={0} max={maxHoles} />
            </div>
            <p className="field-hint">{maxHoles - form.gir} greens missed · GIR: {Math.round((form.gir / maxHoles) * 100)}%</p>
          </div>

          <div className="form-group">
            <label className="form-label">Missed greens — how many each direction?</label>
            <p className="field-hint-top">Enter 0 if no misses in that direction</p>
            <div className="input-row"><span className="input-label">Left of green</span><NumberInput value={form.approach_left} onChange={v => set('approach_left', v)} min={0} max={maxHoles} /></div>
            <div className="input-row" style={{marginTop:8}}><span className="input-label">Right of green</span><NumberInput value={form.approach_right} onChange={v => set('approach_right', v)} min={0} max={maxHoles} /></div>
            <div className="input-row" style={{marginTop:8}}><span className="input-label">Short of green</span><NumberInput value={form.approach_short} onChange={v => set('approach_short', v)} min={0} max={maxHoles} /></div>
            <div className="input-row" style={{marginTop:8}}><span className="input-label">Long of green</span><NumberInput value={form.approach_long} onChange={v => set('approach_long', v)} min={0} max={maxHoles} /></div>
          </div>

          <div className="step-nav">
            <button className="btn-secondary" onClick={back}>← Back</button>
            <button className="btn-orange" onClick={next}>Next →</button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="step-card">
          <div className="step-title">Short Game</div>
          <div className="step-sub">Chipping, pitching & bunker play</div>

          <div className="form-group">
            <label className="form-label">Up & Down opportunities</label>
            <p className="field-hint-top">From off the green — chip/pitch to make par</p>
            <div className="input-row"><span className="input-label">Attempts</span><NumberInput value={form.ud_att} onChange={v => set('ud_att', v)} min={0} max={maxHoles} /></div>
            <div className="input-row" style={{marginTop:8}}><span className="input-label">Converted</span><NumberInput value={form.ud_made} onChange={v => set('ud_made', Math.min(v, form.ud_att))} min={0} max={form.ud_att} /></div>
            {form.ud_att > 0 && <p className="field-hint">Conversion rate: {Math.round((form.ud_made / form.ud_att) * 100)}%</p>}
          </div>

          <div className="form-group">
            <label className="form-label">Bunker play</label>
            <div className="input-row"><span className="input-label">Bunkers played</span><NumberInput value={form.bunkers} onChange={v => set('bunkers', v)} min={0} max={maxHoles} /></div>
            <div className="input-row" style={{marginTop:8}}><span className="input-label">Sand saves made</span><NumberInput value={form.bunker_saves} onChange={v => set('bunker_saves', Math.min(v, form.bunkers))} min={0} max={form.bunkers} /></div>
            {form.bunkers > 0 && <p className="field-hint">Sand save rate: {Math.round((form.bunker_saves / form.bunkers) * 100)}%</p>}
          </div>

          <div className="step-nav">
            <button className="btn-secondary" onClick={back}>← Back</button>
            <button className="btn-orange" onClick={next}>Next →</button>
          </div>
        </div>
      )}

      {step === 5 && (
        <div className="step-card">
          <div className="step-title">Putting</div>
          <div className="step-sub">Break down your putts by type</div>

          <div className="form-group">
            <label className="form-label">Putts breakdown</label>
            <div className="input-row"><span className="input-label">🟢 1-putts (holes out in one)</span><NumberInput value={form.one_putts} onChange={v => set('one_putts', v)} min={0} max={maxHoles} /></div>
            <div className="input-row" style={{marginTop:8}}><span className="input-label">🟡 2-putts</span><NumberInput value={form.two_putts} onChange={v => set('two_putts', v)} min={0} max={maxHoles * 2} /></div>
            <div className="input-row" style={{marginTop:8}}><span className="input-label">🔴 3-putts (or more)</span><NumberInput value={form.three_putts} onChange={v => set('three_putts', v)} min={0} max={maxHoles} /></div>
            <div className="putts-total">
              Total: <strong>{totalPutts} putts</strong>
              <span className={totalPutts <= (maxHoles === 9 ? 14 : 28) ? 'putts-good' : totalPutts <= (maxHoles === 9 ? 16 : 32) ? 'putts-ok' : 'putts-bad'}>
                {totalPutts <= (maxHoles === 9 ? 14 : 28) ? ' · Excellent' : totalPutts <= (maxHoles === 9 ? 16 : 32) ? ' · Good' : ' · Above average'}
              </span>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Main putting issue this round</label>
            <PillGroup
              options={[
                {value:'distance',label:'Distance control'},
                {value:'line',label:'Reading the line'},
                {value:'short',label:'Missing short putts'},
                {value:'yips',label:'Nerves / yips'},
                {value:'none',label:'No major issues'}
              ]}
              value={form.putt_issue} onChange={v => set('putt_issue', v)}
            />
          </div>

          <div className="step-nav">
            <button className="btn-secondary" onClick={back}>← Back</button>
            <button className="btn-orange" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Analyse My Round ⚡'}</button>
          </div>
        </div>
      )}
    </div>
  )
}
